// IntelligenceChain -- ARC's canonical system diagram:
//   Signal -> Diagnosis -> Opportunity -> Recommendation -> Action ->
//   Measurement -> Learning
// with HUMAN APPROVAL between Recommendation and Action ("Martian
// recommends. Human approves where required.").
//
// Geometry and palette follow ARC/chain/intelligence_chain.svg from the
// locked asset package (Clay nodes with ink core; Signal and Learning in
// Clay Deep; Signal-coloured approval connector). It is rendered as live
// markup (an ordered list) so labels are real text, i18n-able and
// accessible, and so it can reflow to a vertical chain on narrow screens.
//
// PRODUCT TRUTH: each stage may carry `truth`:
//   'live'        -- shown as a solid node (the capability exists today)
//   'vision'      -- dashed, hollow node + "Vision" tag
//   'unsupported' -- dotted, muted node + "Not available" tag
// With no `truth` the chain is a neutral model of the system flow and makes
// no claim about availability. Callers surface only what the product truth
// layer supports -- this component never upgrades a stage on its own.
//
// ONE LEAD PER FRAME: this is a system diagram. GLIM may accompany it but
// is never a node in the chain.

import { useEffect, useRef, useState } from 'react';
import './brand.css';

export const INTELLIGENCE_CHAIN_STAGES = Object.freeze([
  { key: 'signal', label: 'Signal' },
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'opportunity', label: 'Opportunity' },
  { key: 'recommendation', label: 'Recommendation' },
  { key: 'action', label: 'Action' },
  { key: 'measurement', label: 'Measurement' },
  { key: 'learning', label: 'Learning' },
]);

const TRUTH_TAG = { vision: 'Vision', unsupported: 'Not available' };
const ANCHOR_KEYS = new Set(['signal', 'learning']);

function usePrefersReducedMotionLocal() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

export function IntelligenceChain({
  stages = INTELLIGENCE_CHAIN_STAGES,
  activeKey = null,
  states = {}, // { [stageKey]: 'complete' | 'active' | 'pending' | 'hidden' }
  truth = {}, // { [stageKey]: 'live' | 'vision' | 'unsupported' }
  approvalLabel = 'Approval',
  showApproval = true,
  tone = 'light',
  compact = false,
  animate = false,
  label = 'Martian Intelligence Chain',
  className = '',
}) {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotionLocal();
  const [inView, setInView] = useState(!animate);

  useEffect(() => {
    if (!animate || reduced) { setInView(true); return undefined; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, [animate, reduced]);

  return (
    <ol
      ref={ref}
      aria-label={label}
      data-testid="intelligence-chain"
      className={[
        'arc-chain',
        compact ? 'arc-chain--compact' : '',
        tone === 'dark' ? 'arc-chain--dark' : '',
        animate ? 'arc-chain--animate' : '',
        animate && inView ? 'is-in' : '',
        className,
      ].filter(Boolean).join(' ')}
    >
      {stages.map((stage, i) => {
        const prev = stages[i - 1];
        const afterApproval = showApproval && prev?.key === 'recommendation' && stage.key === 'action';
        const truthState = truth[stage.key];
        const flow = states[stage.key] || (activeKey === stage.key ? 'active' : null);
        const classes = [
          'arc-chain__item',
          ANCHOR_KEYS.has(stage.key) ? 'arc-chain__item--anchor' : '',
          afterApproval ? 'arc-chain__item--after-approval' : '',
          flow === 'active' ? 'arc-chain__item--active' : '',
          flow === 'pending' ? 'arc-chain__item--pending' : '',
          // 'hidden' lets a caller construct the chain progressively (e.g. the
          // landing story). The stage stays in the DOM and the accessibility tree.
          flow === 'hidden' ? 'arc-chain__item--hidden' : '',
          truthState === 'vision' ? 'arc-chain__item--vision' : '',
          truthState === 'unsupported' ? 'arc-chain__item--unsupported' : '',
        ].filter(Boolean).join(' ');
        return (
          <li
            key={stage.key}
            className={classes}
            style={{ '--i': i }}
            data-stage={stage.key}
            data-truth={truthState || undefined}
            aria-current={flow === 'active' ? 'step' : undefined}
          >
            {afterApproval && (
              <span className="arc-chain__approval" data-testid="chain-approval">
                <span className="arc-chain__approval-dot" aria-hidden="true" />
                {approvalLabel}
              </span>
            )}
            <span className="arc-chain__node" aria-hidden="true" />
            <span className="arc-chain__text">
              <span className="arc-chain__label">{stage.label}</span>
              {TRUTH_TAG[truthState] && <span className="arc-chain__tag">{TRUTH_TAG[truthState]}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default IntelligenceChain;
